/**
 * Group consecutive failed-then-successful ModelEvent retries inside the
 * same `span_id` into a single visible event.
 *
 * Run AFTER `correctRetryTimestamps` so the success event's timestamp is
 * already past its preceding failed siblings (the stable key relies on that
 * post-correction uniqueness).
 *
 * Returns:
 * - `events` — input list with failed retry attempts removed; the success
 *    event survives unchanged.
 * - `attempts` — map keyed by the success event's stable key, holding the
 *    full ordered attempt list (failed first, success last). Single-attempt
 *    calls are not added to the map.
 *
 * When no retries are detected, returns the input array reference unchanged
 * so callers can memoize on identity.
 *
 * Identity guard: a failed event is only treated as a retry of a later
 * successful event when their `model`, `input.length`, `tools` (length +
 * names), and `tool_choice` match. Without this guard, an unrelated failure
 * (e.g. failed model A then a fallback successful call to model B in the
 * same span) would be hidden as a "retry" of an unrelated call.
 */

import type {
  Event,
  ModelEvent,
  ToolChoice,
} from "@tsmono/inspect-common/types";

export const retryAttemptKey = (event: ModelEvent): string => {
  if (event.uuid) return `uuid:${event.uuid}`;
  const epoch = Date.parse(event.timestamp);
  const tsPart = Number.isNaN(epoch) ? event.timestamp : String(epoch);
  return `ts:${event.span_id ?? ""}:${tsPart}`;
};

export interface RetryGroupingResult {
  events: Event[];
  attempts: Map<string, ModelEvent[]>;
}

interface PendingFailed {
  event: ModelEvent;
  index: number;
}

function toolChoiceEqual(a: ToolChoice, b: ToolChoice): boolean {
  if (a === b) return true;
  if (typeof a === "string" || typeof b === "string") return a === b;
  return a.name === b.name;
}

function toolsEqual(a: ModelEvent["tools"], b: ModelEvent["tools"]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.name !== b[i]?.name) return false;
  }
  return true;
}

function isSameCall(failed: ModelEvent, success: ModelEvent): boolean {
  return (
    failed.model === success.model &&
    failed.input.length === success.input.length &&
    toolsEqual(failed.tools, success.tools) &&
    toolChoiceEqual(failed.tool_choice, success.tool_choice)
  );
}

export function groupRetryAttempts(events: Event[]): RetryGroupingResult {
  const attempts = new Map<string, ModelEvent[]>();
  const pendingFailed = new Map<string, PendingFailed[]>();
  const dropIndices = new Set<number>();

  for (let i = 0; i < events.length; i++) {
    const e = events[i]!;
    if (e.event !== "model") continue;

    const m = e;
    const key = m.span_id ?? "";

    if (m.error != null) {
      const run = pendingFailed.get(key) ?? [];
      run.push({ event: m, index: i });
      pendingFailed.set(key, run);
      continue;
    }

    const run = pendingFailed.get(key);
    if (run && run.length > 0) {
      const matches = run.filter((p) => isSameCall(p.event, m));
      if (matches.length > 0) {
        attempts.set(retryAttemptKey(m), [...matches.map((p) => p.event), m]);
        for (const p of matches) dropIndices.add(p.index);
      }
      // Any failed events that do NOT match the success's identity are
      // unrelated failures — leave them visible in the transcript.
      pendingFailed.delete(key);
    }
  }

  if (dropIndices.size === 0) {
    return { events, attempts };
  }

  const filtered: Event[] = [];
  for (let i = 0; i < events.length; i++) {
    if (!dropIndices.has(i)) filtered.push(events[i]!);
  }
  return { events: filtered, attempts };
}
